import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { SuccessNotify, FailedNotify } from "../constants/tostify";
import {
  useGetUserConnectionsQuery,
  useGetUserInfoQuery,
  useUpdateUserProfileMutation,
  useCheckMarketerForQuery,
} from "../features/user/userApiSlice";
import { useGetPurchasesByUserQuery } from "../features/marketing/marketingApiSlice";
import { useGetBusinessTypeRulesByTypeQuery } from "../features/shop/shopApiSlice";
import { useGetAllConnectedQuery } from "../features/connection/connectionApiSlice";

export const useMarketingProfileState = () => {
  const [leftSide, setLeftSidewidth] = useState(7);
  const [rightSide, setRightSide] = useState(5);
  const { name } = useSelector((state) => state.user);
  const [tabsData, setTabsData] = useState([]);
  const [businessRule, setBusinessRule] = useState({});
  const [activeTab, setActiveTab] = useState("");
  const [infooo, setInfooo] = useState();
  const [info, setInfo] = useState();
  const location = useLocation();

  // Mark this session as marketer-profile so Connect from other pages
  // stamps senderBusinessType=marketerProfile.
  useEffect(() => {
    if (location.pathname === "/marketing-profile") {
      localStorage.setItem("previousRoute", "marketing-profile");
    }
  }, [location.pathname]);
  const [createShop, setCreateShop] = useState({
    text: "",
    privacy: "",
  });

  const [loading, setLoading] = useState(true);

  const [createPost, setCreatePost] = useState({
    shopid: "",
    userid: name?._id,
    businessType: "marketerProfile",
    my: [
      {
        heading: "",
        content: "",
        img: [""],
        video: [""],
      },
    ],
  });

  const [currentPlan, setCurrentPlan] = useState(null);

  // Hook 1: Fetch subscription marketing purchases by user
  const { data: purchaseData } = useGetPurchasesByUserQuery(name?._id, {
    skip: !name?._id,
    refetchOnMountOrArgChange: true,
  });

  // Hook 2: Fetch connections data
  const { data: connectionsData, isLoading: connectionsLoading } =
    useGetUserConnectionsQuery(
      {
        userid: name?._id,
        isMarketerProfile: location.pathname === "/marketing-profile",
      },
      {
        skip: !name?._id,
        refetchOnMountOrArgChange: true,
      },
    );

  // Marketer Connectivity list — strip those from right-side BT suggestion tabs
  const { data: marketerConnectedData } = useGetAllConnectedQuery(
    { userid: name?._id, isMarketerProfile: true },
    {
      skip: !name?._id || location.pathname !== "/marketing-profile",
      refetchOnMountOrArgChange: true,
    },
  );

  const connectedHideKeys = useMemo(() => {
    const keys = new Set();
    const addForTab = (id, bt) => {
      if (id == null || id === "" || id === "null") return;
      const sid = String(id);
      const nbt = bt || "generalPublic";
      keys.add(`${sid}|${nbt}`);
      if (nbt === "generalPublic" || nbt === "marketerProfile") {
        keys.add(`${sid}|generalPublic`);
      }
    };

    (marketerConnectedData?.connectedBT || []).forEach((group) => {
      const bt = group?.businessType || "generalPublic";
      (group?.items || []).forEach((item) => {
        if (
          bt === "generalPublic" ||
          bt === "marketerProfile" ||
          !item?.shopid
        ) {
          addForTab(item?.userid, "generalPublic");
          if (bt && bt !== "generalPublic") addForTab(item?.userid, bt);
        } else {
          addForTab(item?.shopid, bt);
          addForTab(item?.userid, bt);
          addForTab(item?.userid, "generalPublic");
        }
      });
    });

    (marketerConnectedData?.connected || []).forEach((conn) => {
      const senderId = conn?.senderid ? String(conn.senderid) : null;
      const receiverId = conn?.recieverid ? String(conn.recieverid) : null;
      const senderShop = conn?.sendershopid ? String(conn.sendershopid) : null;
      const receiverShop = conn?.recievershopid
        ? String(conn.recievershopid)
        : null;
      const me = String(name?._id);
      const recvBT = conn?.businessType || "generalPublic";
      const sendBT = conn?.senderBusinessType || "generalPublic";

      if (senderId === me) {
        if (!receiverShop || recvBT === "generalPublic") {
          addForTab(receiverId, "generalPublic");
        } else {
          addForTab(receiverShop, recvBT);
          addForTab(receiverId, recvBT);
          addForTab(receiverId, "generalPublic");
        }
      }
      if (receiverId === me) {
        if (!senderShop || sendBT === "generalPublic") {
          addForTab(senderId, "generalPublic");
        } else {
          addForTab(senderShop, sendBT);
          addForTab(senderId, sendBT);
          addForTab(senderId, "generalPublic");
        }
      }
    });

    return keys;
  }, [marketerConnectedData, name?._id]);

  // Hook to get marketer info reactively — always refetch on mount so marketerFor
  // is never stale after a hard refresh.
  const { data: marketerData } = useCheckMarketerForQuery(name?._id, {
    skip: !name?._id,
    refetchOnMountOrArgChange: true,
  });

  const ind = marketerData?.data?.MIndustryName?.[0];
  const marketerFor = ind?.name || ind || undefined;

  // Hook 3: Fetch business type rules
  const shouldSkipRules =
    !marketerFor || marketerFor === "undefined" || marketerFor === "null";
  const {
    data: rulesData,
    isLoading: isRulesLoading,
    isError: isRulesError,
  } = useGetBusinessTypeRulesByTypeQuery(marketerFor, {
    skip: shouldSkipRules,
    refetchOnMountOrArgChange: true,
  });

  // Hook 4: Fetch user info
  const { data: userInfoData } = useGetUserInfoQuery(name?._id, {
    skip: !name?._id,
    refetchOnMountOrArgChange: true,
  });

  // Hook 5: Update user profile mutation
  const [updateProfile] = useUpdateUserProfileMutation();

  // Process purchase data
  useEffect(() => {
    if (purchaseData?.data?.planId?.planName) {
      const plans = ["Free", "Basic", "Standard", "Premium"];
      const planName = purchaseData.data.planId.planName;

      if (plans.includes(planName)) {
        setCurrentPlan(planName);
      } else {
        setCurrentPlan(null);
      }
    }
  }, [purchaseData]);

  // isRulesStillLoading must become false once the fetch finishes — whether it
  // succeeded, errored, or was skipped. The previous `!rulesData` check caused
  // an infinite block whenever the rules API returned an error or empty data.
  const isRulesStillLoading =
    marketerFor && !shouldSkipRules
      ? isRulesLoading && !isRulesError // only true while actively fetching
      : false;

  // Process connections and business rules data.
  // Guard: wait until rules have finished (or been skipped) AND connections
  // are not still in-flight before building tabs.
  useEffect(() => {
    // Still waiting for rules to resolve
    if (isRulesStillLoading) return;

    // Still waiting for connections data to arrive for the first time
    if (connectionsLoading && !connectionsData) return;

    if (connectionsData?.data) {
      const responseData = connectionsData.data;
      const dynamicTabs = Object.keys(responseData).map((key) => {
        const rawList = Array.isArray(responseData[key])
          ? responseData[key]
          : [];
        // Already connected as marketer → stay on left Connectivity only
        const data = rawList.filter((item) => {
          const profileId = item?._id != null ? String(item._id) : "";
          const profileUserId =
            item?.userid != null ? String(item.userid) : profileId;
          const tabKey = key || "generalPublic";
          if (
            connectedHideKeys.has(`${profileId}|${tabKey}`) ||
            connectedHideKeys.has(`${profileUserId}|${tabKey}`)
          ) {
            return false;
          }
          return true;
        });

        return {
          eventKey: key,
          title:
            key.charAt(0).toUpperCase() +
            key.slice(1).replace(/([A-Z])/g, " $1"),
          data,
        };
      });

      const rules = rulesData?.data || {};

      setBusinessRule(rules);
      setTabsData(dynamicTabs);

      if (dynamicTabs.length > 0) {
        setActiveTab((prev) => prev || "home");
      }
      setLoading(false);
    } else if (!connectionsLoading) {
      // Connections finished loading but returned no data — still unblock loading
      // so the UI doesn't stay stuck on skeleton indefinitely.
      setLoading(false);
    }
  }, [
    connectionsData,
    connectionsLoading,
    rulesData,
    location.pathname,
    marketerFor,
    isRulesStillLoading,
    connectedHideKeys,
  ]);

  // Process user info data
  useEffect(() => {
    if (userInfoData?.data?.User) {
      setInfooo(userInfoData.data.User);
    }
  }, [userInfoData]);

  const handleTabSelect = (eventKey) => {
    setActiveTab(eventKey);
  };

  const getActiveTabData = () => {
    const activeTabData = tabsData.find((tab) => tab.eventKey === activeTab);
    return activeTabData ? activeTabData.data : [];
  };

  const inputChange = (event) => {
    setCreateShop((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const userDatahandler = async () => {
    try {
      const response = await updateProfile({
        id: name?._id,
        data: {
          description: createShop.text,
        },
      }).unwrap();

      if (response?.success) {
        SuccessNotify("Successfully!");
        setInfo(response?.data?.User);
        if (response?.data?.User) {
          setInfooo(response.data.User);
        }
      }
    } catch (e) {
      console.log("error", e);
      FailedNotify("Failed! Try Again");
    }
  };

  const widthHandler = () => {
    if (leftSide === 7 && rightSide === 5) {
      setLeftSidewidth(2);
      setRightSide(10);
    } else {
      setLeftSidewidth(7);
      setRightSide(5);
    }
  };

  return {
    leftSide,
    rightSide,
    name,
    tabsData,
    businessRule,
    activeTab,
    infooo,
    setInfooo,
    info,
    location,
    createShop,
    setCreateShop,
    loading,
    setLoading,
    createPost,
    setCreatePost,
    currentPlan,
    handleTabSelect,
    getActiveTabData,
    inputChange,
    userDatahandler,
    widthHandler,
    marketerFor,
  };
};
